import {
  Box,
  Button,
  Icon,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuSeparator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import type { ReactElement } from "react";

import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { useAnalytics, AnalyticsContext, WorkspaceEvent } from "#app/lib/analytics";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import { useCustomFieldsDialogActions } from "#app/lib/custom-fields-dialog-context";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { useIntegrationsDialogActions } from "#app/lib/integrations-dialog-context";
import {
  usePermission,
  useViewerCanBrowseMembers,
} from "#app/lib/hooks/use-permission";
import { useRouter } from "#app/lib/hooks/router-compat";
import { orderWorkspaces } from "#app/lib/workspace-picker";
import { readWorkspaceRecents } from "#app/lib/workspace-recents";
import { useSettingsDoors } from "#app/components/navigation/use-settings-doors";
import { useManageDialogDoors } from "#app/components/navigation/use-manage-dialog-doors";
import { memberCountLabel } from "#app/lib/utils/member-count";
import { RECENTS_SECTION_ROWS } from "#app/lib/constants/recents";


/** Largest member count the menu spells out exactly; above it "999+". */
const MEMBER_COUNT_CAP = 999;

export function WorkspaceMenuContent({
  onClose,
}: {
  onClose: () => void;
}): ReactElement {
  const router = useRouter();
  const { enterSettings } = useSettingsDoors();
  const { activeWorkspace, workspaces, switchWorkspace } = useWorkspace();
  const { track } = useAnalytics();
  const { openDialog: openAutomations } = useAutomationsDialogActions();
  const { openDialog: openCustomFields } = useCustomFieldsDialogActions();
  const { openDialog: openIntegrations } = useIntegrationsDialogActions();
  const {
    showCustomFields: canManageCustomFields,
    showAutomations,
    showIntegrations,
  } = useManageDialogDoors();
  const canManageUsers = usePermission("manage.users");
  // The Members door leads to a member surface (the directory, or the members
  // settings page); a share-only viewer has neither, so Settings takes the
  // whole row.
  const canBrowseMembers = useViewerCanBrowseMembers();
  // create.roles gates the workspace settings page (see ADMINISTRATION_ITEMS
  // in settings-sidebar.tsx), so Settings deep-links exactly when the page
  // would appear in the sidebar.
  const canManageWorkspace = usePermission("create.roles");
  // The Manage group only renders when the viewer holds at least one door;
  // a viewer with none sees no label and no separator.
  const hasManageSection =
    showAutomations || canManageCustomFields || showIntegrations;
  // The menu never lists everything: the workspaces this device opened most
  // recently, capped, then one door to the picker (where search lives).
  const otherWorkspaces = orderWorkspaces(
    workspaces.filter(
      (workspace) =>
        workspace.workspaceId !== activeWorkspace?.workspaceId &&
        workspace.memberStatus === "active",
    ),
    readWorkspaceRecents(),
  );
  const shownWorkspaces = otherWorkspaces.slice(0, RECENTS_SECTION_ROWS);
  const hasMoreWorkspaces = otherWorkspaces.length > shownWorkspaces.length;

  const closeThen = (action: () => void) => () => {
    onClose();
    action();
  };

  const handleSwitchWorkspace = async (workspace: Workspace) => {
    onClose();
    const success = await switchWorkspace(workspace.workspaceId);
    if (!success) return;
    // After the switch, so the event bills to the workspace it opened.
    track({ context: AnalyticsContext.Workspace, event: WorkspaceEvent.Switch, data: { via: "menu" } });
    router.replace(`/(app)/${workspace.urlPath}`);
  };

  return (
    <>
      {/* Identity header: 40px tile, 12px to a two-line column (14/20 medium
        * name over 12px medium faint details, 2px apart), 12px inset all round, and
        * 12px down to the 32px action pair. */}
      <Box gap="base" padding="base">
        <Box align="center" direction="row" gap="base">
          <WorkspaceAvatar
            avatarColor={activeWorkspace?.avatarColor}
            logo={activeWorkspace?.settings?.logo}
            name={activeWorkspace?.name ?? "Workspace"}
            size="md"
          />
          <Box direction="column" gap="2xs" minWidth={0}>
            <Text size="sm" truncate weight="medium">
              {activeWorkspace?.name ?? "Workspace"}
            </Text>
            {activeWorkspace ? (
              <Text size="xs" variant="faint" weight="medium">
                {memberCountLabel(activeWorkspace.memberCount, { cap: MEMBER_COUNT_CAP })}
              </Text>
            ) : null}
          </Box>
        </Box>

        <Box direction="row" gap="sm">
          <Button
            className="flex-1"
            onClick={closeThen(() =>
              enterSettings(() =>
                router.replace(
                  `/(app)/${activeWorkspace?.urlPath}/${
                    canManageWorkspace ? "settings/workspace" : "settings"
                  }`,
                ),
              ),
            )}
            shape="square"
            variant="outline"
          >
            <Icon color="muted" name="settings" size="sm" />
            <Text variant="muted">Settings</Text>
          </Button>
          {canBrowseMembers ? (
            <Button
              className="flex-1"
              onClick={closeThen(() =>
                router.replace(
                  `/(app)/${activeWorkspace?.urlPath}/${
                    canManageUsers ? "settings/members" : "members"
                  }`,
                ),
              )}
              shape="square"
              variant="outline"
            >
              <Icon color="muted" name="users" size="sm" />
              <Text variant="muted">Members</Text>
            </Button>
          ) : null}
        </Box>
      </Box>

      {hasManageSection ? (
        <>
          <MenuSeparator />
          <Box paddingX="sm" paddingY="sm">
            <MenuGroup>
              <MenuGroupLabel>Manage</MenuGroupLabel>
              {canManageCustomFields ? (
                <MenuItem
                  onClick={closeThen(() => openCustomFields())}
                  size="lg"
                >
                  <Icon color="muted" name="swatch-book" size="md" />
                  <Text>Custom Fields</Text>
                </MenuItem>
              ) : null}
              {showAutomations ? (
                <MenuItem
                  onClick={closeThen(() => openAutomations())}
                  size="lg"
                >
                  <Icon color="muted" name="zap" size="md" />
                  <Text>Automations</Text>
                </MenuItem>
              ) : null}
              {showIntegrations ? (
                <MenuItem
                  onClick={closeThen(() => openIntegrations())}
                  size="lg"
                >
                  <Icon color="muted" name="puzzle" size="md" />
                  <Text>App Center</Text>
                </MenuItem>
              ) : null}
            </MenuGroup>
          </Box>
        </>
      ) : null}

      <MenuSeparator />
      <Box gap="xs" paddingX="sm" paddingY="sm">
        {otherWorkspaces.length > 0 ? (
          <MenuGroup>
            <MenuGroupLabel>Switch Workspaces</MenuGroupLabel>
            {shownWorkspaces.map((workspace) => (
              <MenuItem
                key={workspace.workspaceId}
                onClick={() => void handleSwitchWorkspace(workspace)}
                size="lg"
              >
                {/* A Base UI menu item registers only as a DIRECT popup
                  * child, so the tooltip roots INSIDE the row. */}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Box
                        align="center"
                        direction="row"
                        fill="width"
                        gap="sm"
                      />
                    }
                  >
                    <WorkspaceAvatar
                      avatarColor={workspace.avatarColor}
                      logo={workspace.settings?.logo}
                      name={workspace.name}
                    />
                    <Text size="sm" weight="medium">
                      {workspace.name}
                    </Text>
                  </TooltipTrigger>
                  {/* The trigger fills the row, so the bubble anchors at the
                    * row's right edge: 8px item inset + 8px section inset +
                    * the popup border sit between it and the menu's outer
                    * edge, and the offset clears them with a 4px gap. */}
                  <TooltipPopup side="right" sideOffset={21}>
                    {memberCountLabel(workspace.memberCount, { cap: MEMBER_COUNT_CAP })}
                  </TooltipPopup>
                </Tooltip>
              </MenuItem>
            ))}
            {hasMoreWorkspaces ? (
              <MenuItem
                onClick={closeThen(() =>
                  router.push("/(app)/workspace-select"),
                )}
                size="lg"
              >
                <Icon color="muted" name="layout-grid" size="md" />
                <Box direction="row" align="center" justify="between" gap="sm" fill="width">
                  <Text size="sm" variant="muted">
                    All workspaces
                  </Text>
                  <Text size="xs" variant="muted">
                    {workspaces.filter((w) => w.memberStatus === "active").length}
                  </Text>
                </Box>
              </MenuItem>
            ) : null}
          </MenuGroup>
        ) : null}
        <Box className="mt-1.5" padding="sm">
          <Button
            fullWidth
            onClick={closeThen(() =>
              router.push("/(app)/workspace-select"),
            )}
            shape="square"
            variant="outline"
          >
            <Icon color="muted" name="plus" size="sm" />
            <Text size="sm" variant="muted">
              Create or Join Workspace
            </Text>
          </Button>
        </Box>
      </Box>
    </>
  );
}
